import { Map, OrderedMap, OrderedSet, Seq } from 'immutable';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { iFullStoreState, Units } from '../../../shared/interfaces';
import { IReducerDevicesTripsPoints } from '../../../stores/reducers/devicesTripsPoints';
import InfoSelectedPoint from '../InfoSelectedPoint';
import { MapTripsProvider } from '../store/MapTripsContext';
import DeviceTrip from './DeviceTrip';

type DeviceTripsProps = {
    deviceId: string;
    isReportTrips?: boolean;
}

const DeviceTrips = ({deviceId, isReportTrips = false}: DeviceTripsProps) => {
    const deviceColor = useSelector<iFullStoreState, string>(
        state => state.devicesData.devicesDetails.getIn([deviceId, 'color'], '#000') as string,
    );
    const deviceUnits = useSelector<iFullStoreState, Units>(
        state => state.devicesData.devicesDetails.getIn([deviceId, 'units'], Units.miles) as Units,
    );
    const deviceLastTripId = useSelector<iFullStoreState, string | undefined>(
        state => state.devicesData.devicesLastPing.getIn([deviceId, 'tripId']) as string | undefined,
    );

    const tripsIds = useSelector<iFullStoreState, Seq.Indexed<string>>(
        s => {
            if (!isReportTrips) {
                return (s.devicesTripsPoints.getIn([deviceId, 'tripsPoints'], OrderedMap()) as OrderedMap<string, any>).keySeq();
            }
            const reportTripsIds: Map<string, boolean | string> = s.devicesTripsPoints.getIn([deviceId, 'reportTripsIds']) as Map<string, boolean | string>;
            return reportTripsIds.filter((show) => !!show).keySeq();
        },
        (left, right) => left.size === right.size,//is(left, right))
    );
    // filtered trip ids of device where value is true

    const liveTripsIds = useSelector<iFullStoreState, OrderedSet<string>>(
        s => s.devicesTripsPoints.getIn([deviceId, 'liveTripsIds']) as OrderedSet<string>,
        (left, right) => left.size === right.size,//is(left, right))
    );

    const highlightTripPointIds = useSelector<iFullStoreState, Map<string, string>>(
        s => (s.devicesTripsPoints.getIn([deviceId, 'reportTripsIds'], Map()) as Map<string, any>)
            .filter((highlightPointId) => typeof highlightPointId === 'string'),
    );
    const devicesTripsPoints = useSelector<iFullStoreState, IReducerDevicesTripsPoints>(
        s => s.devicesTripsPoints,
    );
    const deviceDetails = useSelector<iFullStoreState, Map<string, object>>(s => s.devicesData.devicesDetails);

    const tripsDetails = useMemo(() => {
        const result: { tripId: string; deviceId: string; color: string }[] = [];

        devicesTripsPoints.entrySeq().forEach(([dId, deviceTrips]: any) => {
            const reportTripsIds: Map<string, boolean | string> = deviceTrips.get('reportTripsIds', Map());

            reportTripsIds.forEach((show, reportTripId) => {
                if (!show) return;

                result.push({
                    tripId: reportTripId,
                    deviceId: dId,
                    color: deviceDetails.getIn([dId, 'color'], '#000') as string,
                });
            });
        });

        return result;
    }, [devicesTripsPoints, deviceDetails]);


    const firstTripId = tripsIds.first<undefined>();
    const lastTripId = tripsIds.last<undefined>();

    return <MapTripsProvider>
        {tripsIds.toArray().map((id, index) =>
            <DeviceTrip key={id}
                        tripsDetails={tripsDetails}
                        isReportTrip={isReportTrips}
                        last={deviceLastTripId === id}
                        tripIdx={index}
                        tripId={id}
                        highlightPointId={isReportTrips ? highlightTripPointIds.get(id) : undefined}
                        deviceId={deviceId}
                        color={deviceColor}
                        units={deviceUnits}
                        live={liveTripsIds.includes(id)}
            />,
        )}
        {(tripsIds.toArray().length > 0) && <InfoSelectedPoint firstTripId={firstTripId} lastTripId={lastTripId} />}
    </MapTripsProvider>;
};

export default React.memo(DeviceTrips);

